import { useNavigate } from 'react-router-dom';
import { ButtonBase, Chip, Tooltip, alpha } from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { getCrmPalette } from '../theme/brand';
import { useThemeMode } from '../theme/ThemeModeContext';
import { useGetWalletMeQuery } from '../store/api/walletApi';
import { useGetPaisDelTenantQuery } from '../store/api/monedasApi';
import { usePermissions } from '../hooks/usePermissions';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

const P = getCrmPalette();
const golden = P.brand.golden;

const fmt = (n: number) =>
  n.toLocaleString('es-UY', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export function MoneyWalletChip() {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  // En claro, dorado más oscuro para el texto/ícono (mejor contraste sobre fondo claro).
  const goldText = mode === 'light' ? P.brand.goldenDark : golden;
  const { hasPermission } = usePermissions();
  const walletEnabled = useFeatureFlag('wallet');
  const canRead = hasPermission('wallet:read');

  const { data } = useGetWalletMeQuery(undefined, { skip: !canRead || !walletEnabled });
  const { data: pais } = useGetPaisDelTenantQuery(undefined, { skip: !canRead || !walletEnabled });

  if (!walletEnabled || !canRead || !data) return null;

  /**
   * El saldo en pesos uruguayos solo se muestra en Uruguay.
   *
   * El wallet tiene dos saldos, UYU y USD, porque es la plata de Imperium; un tenant de
   * otro país nunca va a tener pesos uruguayos ahí, y el chip le mostraba "$0 UYU" fijo.
   * Sin dato de país todavía se asume Uruguay, que es lo que existe hoy. (02/09/2026)
   */
  const muestraUYU = !pais || pais.countryIso === 'UY';
  const hasUYU = muestraUYU && data.balanceUYU > 0;
  const hasUSD = data.balanceUSD > 0;

  const label = hasUYU && hasUSD
    ? `$${fmt(data.balanceUYU)} · U$S ${fmt(data.balanceUSD)}`
    : hasUSD || !muestraUYU
    ? `U$S ${fmt(data.balanceUSD)}`
    : `$${fmt(data.balanceUYU)}`;

  const tooltip = muestraUYU
    ? `Wallet — $${fmt(data.balanceUYU)} UYU · U$S ${fmt(data.balanceUSD)} USD`
    : `Wallet — U$S ${fmt(data.balanceUSD)} USD`;

  return (
    <Tooltip title={tooltip}>
      <ButtonBase onClick={() => navigate('/wallet')} sx={{ borderRadius: 999 }}>
        <Chip
          size="small"
          clickable
          icon={<AccountBalanceWalletIcon sx={{ fontSize: 13 }} />}
          label={label}
          sx={{
            height: 26,
            bgcolor: alpha(goldText, 0.1),
            color: goldText,
            fontWeight: 700,
            fontSize: 11.5,
            border: `1px solid ${alpha(goldText, 0.3)}`,
            '& .MuiChip-icon': { color: goldText },
            maxWidth: 240,
            cursor: 'pointer',
            '&:hover': { bgcolor: alpha(golden, 0.18) },
          }}
        />
      </ButtonBase>
    </Tooltip>
  );
}
